/**
 * 轻量 Markdown 工具：渲染 + 内容形态识别
 *
 * 设计约束：
 * 1. 零新增依赖 —— 解析器自研，代码高亮复用项目已有的 prismjs
 * 2. 面向 API 文档场景 —— 覆盖标题/代码块/表格/列表/引用/链接/图片等常用语法
 * 3. 输出经过 HTML 转义，避免文档内容引入 XSS
 *
 * 说明：管理端（saas-manager-web）文档编辑器已改为 Markdown，但历史文档仍是
 * 富文本 HTML，因此对外统一提供 renderDocContent 做「HTML 原样输出 / Markdown 渲染」分发。
 */
import Prism from 'prismjs'
// php 依赖 markup-templating，必须先于 php 引入
import 'prismjs/components/prism-markup-templating'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-csharp'

const ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}

// eslint 的 no-control-regex 会拦截正则里的控制字符，这里用 charCode 构造，避免出现在正则字面量中
const NUL = String.fromCharCode(0)
/** 围栏代码块占位符（NUL 不会出现在正常文本中） */
const CB_PREFIX = `${NUL}CB`
const CB_SUFFIX = NUL
/** 行内代码占位符 */
const IC_PREFIX = `${NUL}IC`
const IC_SUFFIX = NUL

/** markdown 代码语言别名 -> prism 语言 key */
const LANG_ALIAS = {
  js: 'javascript',
  jsx: 'javascript',
  node: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  py: 'python',
  yml: 'yaml',
  html: 'markup',
  xml: 'markup',
  vue: 'markup',
  'c#': 'csharp',
  cs: 'csharp',
  golang: 'go'
}

export function escapeHtml (str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, ch => ESCAPE_MAP[ch])
}

/** 过滤危险协议，保留 http(s)/相对路径/锚点/mailto */
function safeUrl (url) {
  const value = String(url || '').trim()
  if (!value) return '#'
  // 解码后判断，防止 java&#115;cript: 之类的变体绕过
  const normalized = value.replace(/&#(\d+);/g, (m, code) => String.fromCharCode(code)).toLowerCase()
  if (/^(javascript|vbscript|file):/.test(normalized)) return '#'
  return value
}

/** 图片 src：额外允许 data:image（粘贴截图产生的 base64） */
function safeImageUrl (url) {
  const value = String(url || '').trim()
  if (!value) return ''
  if (/^data:image\//i.test(value)) return value
  return safeUrl(value)
}

/** 高亮渲染单个代码块 */
function renderCodeBlock (block) {
  const lang = LANG_ALIAS[block.lang] || block.lang
  const grammar = lang && Prism.languages[lang]
  const code = block.code.replace(/\n+$/, '')
  if (!grammar) {
    return `<pre class="language-text"><code>${escapeHtml(code)}</code></pre>`
  }
  return `<pre class="language-${lang}"><code class="language-${lang}">${Prism.highlight(code, grammar, lang)}</code></pre>`
}

/**
 * 行内语法渲染
 * 注意：入参必须是已转义的文本，本函数内部不再重复转义
 */
function renderInline (text) {
  if (!text) return ''
  let out = String(text)
  const inlineCodes = []

  // 行内代码优先抽出，避免内部字符被后续规则误伤
  out = out.replace(/`([^`\n]+)`/g, (match, code) => {
    inlineCodes.push(code)
    return `${IC_PREFIX}${inlineCodes.length - 1}${IC_SUFFIX}`
  })

  // 自动链接 <https://xxx>
  out = out.replace(/&lt;((?:https?|mailto):[^\s&]+)&gt;/g, (match, url) => {
    return `<a href="${safeUrl(url)}" target="_blank" rel="noopener noreferrer">${url}</a>`
  })

  // 图片 ![alt](src "title")，url 允许一层括号（如 data:image(...)、含括号的路径）
  out = out.replace(/!\[([^\]]*)\]\(((?:[^()\s]|\([^()]*\))+)(?:\s+&quot;([^&]*)&quot;)?\)/g, (match, alt, src, title) => {
    const url = safeImageUrl(src)
    if (!url) return ''
    return `<img src="${url}" alt="${alt}"${title ? ` title="${title}"` : ''} />`
  })

  // 链接 [text](href)
  out = out.replace(/\[([^\]]*)\]\(((?:[^()\s]|\([^()]*\))+)(?:\s+&quot;([^&]*)&quot;)?\)/g, (match, label, href, title) => {
    return `<a href="${safeUrl(href)}"${title ? ` title="${title}"` : ''} target="_blank" rel="noopener noreferrer">${label}</a>`
  })

  // 加粗 **text** / __text__
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>')
  // 删除线 ~~text~~
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>')
  // 斜体 *text*（下划线形式容易误伤 snake_case 变量名，不支持）
  out = out.replace(/(^|[^*\w])\*([^*\n]+)\*/g, '$1<em>$2</em>')

  // 还原行内代码
  out = out.replace(new RegExp(`${IC_PREFIX}(\\d+)${IC_SUFFIX}`, 'g'), (match, index) => {
    return `<code class="md-inline-code">${inlineCodes[Number(index)]}</code>`
  })

  return out
}

function isBlank (line) {
  return !String(line).trim()
}

/** 判断一行是否为新的块级元素起点 */
function isBlockStart (line) {
  return (
    new RegExp(`^${CB_PREFIX}\\d+${CB_SUFFIX}$`).test(String(line).trim()) ||
    /^#{1,6}\s+/.test(line) ||
    /^\s*>/.test(line) ||
    /^\s*([-*+]|\d+\.)\s+/.test(line) ||
    /^\s*\|/.test(line) ||
    /^\s*([-*_])(\s*\1){2,}\s*$/.test(line)
  )
}

/** 拆分表格行：| a | b | -> ['a', 'b'] */
function splitTableRow (line) {
  return String(line)
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
}

function parseTable (lines, start) {
  const headers = splitTableRow(lines[start])
  const aligns = splitTableRow(lines[start + 1]).map(cell => {
    const value = cell.trim()
    if (/^:--?:?$|^:-+:$/.test(value)) return 'center'
    if (/^-+:$/.test(value)) return 'right'
    if (/^:-+$/.test(value)) return 'left'
    return ''
  })

  let html = '<table><thead><tr>'
  headers.forEach((cell, index) => {
    const style = aligns[index] ? ` style="text-align:${aligns[index]}"` : ''
    html += `<th${style}>${renderInline(escapeHtml(cell))}</th>`
  })
  html += '</tr></thead><tbody>'

  let cursor = start + 2
  while (cursor < lines.length && /^\s*\|/.test(lines[cursor])) {
    const cells = splitTableRow(lines[cursor])
    html += '<tr>'
    cells.forEach((cell, index) => {
      const style = aligns[index] ? ` style="text-align:${aligns[index]}"` : ''
      html += `<td${style}>${renderInline(escapeHtml(cell))}</td>`
    })
    html += '</tr>'
    cursor += 1
  }
  html += '</tbody></table>'
  return { html, next: cursor }
}

/**
 * 解析列表（有序/无序，支持任务列表与缩进续行）
 * 缩进产生的嵌套内容会作为上一项的续行渲染，保证内容不丢失
 */
function parseList (lines, start) {
  const firstLine = lines[start]
  const ordered = /^\s*\d+\.\s+/.test(firstLine)
  const itemReg = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/
  const baseIndent = firstLine.match(/^\s*/)[0].length

  const items = []
  let cursor = start
  let current = null

  while (cursor < lines.length) {
    const line = lines[cursor]
    if (isBlank(line)) {
      // 空行后若仍是列表项或缩进内容，视为列表延续
      const nextLine = lines[cursor + 1]
      if (nextLine && (itemReg.test(nextLine) || /^\s{2,}\S/.test(nextLine))) {
        cursor += 1
        continue
      }
      break
    }

    const indent = line.match(/^\s*/)[0].length
    if (itemReg.test(line) && indent <= baseIndent + 1) {
      if (current) items.push(current)
      current = [line.replace(itemReg, '')]
      cursor += 1
    } else if (current && indent > baseIndent) {
      // 缩进续行：合并到当前列表项
      current.push(line.trim())
      cursor += 1
    } else {
      break
    }
  }
  if (current) items.push(current)

  const tag = ordered ? 'ol' : 'ul'
  const body = items
    .map(itemLines => {
      let content = escapeHtml(itemLines.join('\n'))
      // 任务列表 - [ ] / - [x]
      const taskMatch = /^\[([ xX])\]\s*/.exec(content)
      if (taskMatch) {
        const checked = taskMatch[1].toLowerCase() === 'x'
        content = content.replace(taskMatch[0], '')
        return `<li class="md-task-item"><input type="checkbox" disabled${checked ? ' checked' : ''} /> ${renderInline(content)}</li>`
      }
      return `<li>${renderInline(content)}</li>`
    })
    .join('')

  return { html: `<${tag}>${body}</${tag}>`, next: cursor }
}

/** 块级渲染主循环 */
function renderBlocks (src, codeBlocks) {
  const lines = String(src).split(/\r?\n/)
  const blockReg = new RegExp(`^${CB_PREFIX}(\\d+)${CB_SUFFIX}$`)
  let html = ''
  let cursor = 0

  while (cursor < lines.length) {
    const line = lines[cursor]

    if (isBlank(line)) {
      cursor += 1
      continue
    }

    // 还原围栏代码块（占位符可能来自用户手写的 \u0000 字符，需校验索引有效性）
    const blockMatch = blockReg.exec(line.trim())
    if (blockMatch) {
      const block = codeBlocks[Number(blockMatch[1])]
      if (block) {
        html += renderCodeBlock(block)
        cursor += 1
        continue
      }
      // 索引无效：按普通文本渲染，避免内容丢失
      html += `<p>${renderInline(escapeHtml(line.trim().split(NUL).join('')))}</p>`
      cursor += 1
      continue
    }

    // 标题
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1].length
      html += `<h${level}>${renderInline(escapeHtml(heading[2]))}</h${level}>`
      cursor += 1
      continue
    }

    // 分割线
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      html += '<hr />'
      cursor += 1
      continue
    }

    // 引用
    if (/^\s*>/.test(line)) {
      const quoted = []
      while (cursor < lines.length && /^\s*>/.test(lines[cursor])) {
        quoted.push(lines[cursor].replace(/^\s*>\s?/, ''))
        cursor += 1
      }
      html += `<blockquote>${renderBlocks(quoted.join('\n'), codeBlocks)}</blockquote>`
      continue
    }

    // 表格：当前行含 | 且下一行为分隔行
    if (/^\s*\|/.test(line) && cursor + 1 < lines.length && /^\s*\|[\s:|-]+\|?\s*$/.test(lines[cursor + 1]) && /-/.test(lines[cursor + 1])) {
      const table = parseTable(lines, cursor)
      html += table.html
      cursor = table.next
      continue
    }

    // 列表
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const list = parseList(lines, cursor)
      html += list.html
      cursor = list.next
      continue
    }

    // 普通段落：连续的非块级行合并为一段，单换行转 <br />
    const paragraph = []
    while (cursor < lines.length && !isBlank(lines[cursor]) && !isBlockStart(lines[cursor])) {
      paragraph.push(lines[cursor])
      cursor += 1
    }
    if (paragraph.length) {
      html += `<p>${paragraph.map(item => renderInline(escapeHtml(item))).join('<br />')}</p>`
    } else {
      // 兜底，防止极端情况下死循环
      cursor += 1
    }
  }

  return html
}

/**
 * Markdown -> HTML
 * @param {string} source markdown 原文
 * @returns {string} HTML 字符串
 */
export function renderMarkdown (source) {
  if (!source) return ''
  const raw = String(source)

  // 1. 先抽出围栏代码块，避免块内内容被行内规则解析
  const codeBlocks = []
  const withoutCode = raw.replace(/```([\w+#-]*)[ \t]*\r?\n?([\s\S]*?)```/g, (match, lang, code) => {
    codeBlocks.push({ lang: String(lang || '').toLowerCase(), code })
    return `\n${CB_PREFIX}${codeBlocks.length - 1}${CB_SUFFIX}\n`
  })

  // 2. 块级解析使用原始文本（>、#、| 等块标记需在转义前识别），行内内容在渲染时才转义
  return renderBlocks(withoutCode, codeBlocks)
}

/**
 * 内容形态识别：判断已有内容是富文本(HTML)还是 Markdown
 *
 * 识别策略（纯前端，无需后端配合）：
 * 1. 以块级 HTML 标签开头 —— 富文本编辑器产物必然如此，最强特征
 * 2. HTML 标签占比超阈值 —— 富文本夹带少量文字时也能识别
 * 3. 存在明确 Markdown 结构特征时提高阈值，避免 Markdown 内嵌 <br> 等标签被误判
 * 4. 其余情况（含空内容）视为 Markdown
 */
const BLOCK_TAG_REG = /<\s*\/?\s*(p|div|table|thead|tbody|tr|td|th|ul|ol|li|h[1-6]|img|figure|figcaption|section|article|blockquote|pre|span|font)\b[^>]*>/gi
/** Markdown 中常见的内嵌标签，统计前先剔除 */
const INLINE_TAG_REG = /<\s*\/?\s*(br|hr|b|i|u|em|strong|code|sub|sup)\s*\/?>/gi

function hasMarkdownFeature (text) {
  return (
    /^#{1,6}\s+\S/m.test(text) ||
    /```/.test(text) ||
    /^\s*\|.*\|\s*$/m.test(text) ||
    /^\s*([-*+]|\d+\.)\s+\S/m.test(text)
  )
}

export function isHtmlContent (content) {
  const text = String(content == null ? '' : content).trim()
  if (!text) return false
  if (/^<(p|div|h[1-6]|ul|ol|table|figure|section|span|img|strong|font)\b/i.test(text)) return true

  const stripped = text.replace(INLINE_TAG_REG, '')
  const matches = stripped.match(BLOCK_TAG_REG)
  if (!matches || !matches.length) return false
  const tagLength = matches.reduce((sum, tag) => sum + tag.length, 0)
  return tagLength / text.length > (hasMarkdownFeature(text) ? 0.3 : 0.02)
}

/**
 * 判断内容应使用的编辑器
 * @param {string} content 已有内容
 * @returns {'html' | 'markdown'} 富文本用 html，其余（含空内容、历史无内容）用 markdown
 */
export function detectContentFormat (content) {
  return isHtmlContent(content) ? 'html' : 'markdown'
}

/**
 * 富文本 HTML 归一化：链接统一为协议相对地址（与站点协议保持一致）
 * 仅用于处理历史富文本内容，Markdown 渲染结果无需处理（相对链接会被误伤）
 * @param {string} html 富文本 HTML
 * @returns {string}
 */
export function normalizeRichTextHtml (html) {
  return String(html || '').replace(/href="(http:\/\/|https:\/\/|)/g, 'href="//')
}

/**
 * 文档内容渲染入口：历史富文本原样输出，Markdown 渲染为 HTML
 * 富文本分支保持原文不变（不改写链接等），由调用方按需二次处理
 * @param {string} content 文档正文 / 接口参数说明
 * @returns {string} HTML 字符串
 */
export function renderDocContent (content) {
  const raw = String(content == null ? '' : content)
  if (!raw.trim()) return ''
  return isHtmlContent(raw) ? raw : renderMarkdown(raw)
}

/**
 * 文档内容转纯文本（搜索结果摘要等单行场景使用）
 * @param {string} content 文档内容
 * @param {number} [limit] 截断长度
 * @returns {string}
 */
export function toPlainText (content, limit) {
  const raw = String(content == null ? '' : content)
  if (!raw.trim()) return ''

  let text = raw
  if (isHtmlContent(raw)) {
    if (typeof DOMParser !== 'undefined') {
      // DOMParser 解析出的文档不会加载资源、不会执行脚本
      const doc = new DOMParser().parseFromString(
        raw.replace(/<\s*\/\s*(p|div|li|tr|h[1-6]|blockquote)\s*>/gi, ' '),
        'text/html'
      )
      text = (doc.body && doc.body.textContent) || ''
    } else {
      text = raw.replace(/<[^>]*>/g, ' ')
    }
  } else {
    text = raw
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^\s*#{1,6}\s+/gm, '')
      .replace(/^\s*([-*+]|\d+\.)\s+/gm, '')
      .replace(/[*_~`>]/g, '')
      .replace(/\|/g, ' ')
  }

  text = text.replace(/\s+/g, ' ').trim()
  return limit && text.length > limit ? `${text.slice(0, limit)}...` : text
}

export default renderMarkdown
